/**
 * AddPlaybackTokenGeneratorCtrl controller
 * Add/Edit Playback Token Generator
 */

import { PLAYBACK_TOKEN_GENERATOR_TYPES } from 'app/core/constants'

/**
 * AddPlaybackTokenGeneratorCtrl controller
 *
 * @class AddPlaybackTokenGeneratorCtrl
 * @memberof mwApp/settings
 * @see mwApp/settings
 *
 * @ngdoc controller
 * @name mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
 */
/* @ngInject */
export default function AddPlaybackTokenGeneratorCtrl ($uibModalInstance, ptg) {
  var vm = this

  /**
   * Playback Token Generators types
   *
   * @memberof mwApp/catchups.controller:AddPlaybackTokenGeneratorCtrl
   * @type {Object}
   */
  vm.token_generators_types = PLAYBACK_TOKEN_GENERATOR_TYPES

  /**
   * Playback Token Generator
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @type {models.PlaybackTokenGenerator}
   */
  vm.ptg = ptg

  /**
   * isNew flag
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @type {Boolean}
   */
  vm.isNew = ptg._isNew()

  /**
   * Secret Key UI limit (matches max-length="64" on the token field).
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @type {Number}
   */
  vm.secretKeyMaxLength = 64

  /**
   * Live character count from input-with-counter (Playback Token Secret Key only).
   * Catches paste desync when the red counter updates before form.token / vm.ptg.token.
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @type {Number}
   */
  vm.secretKeyCounterLength = 0

  /**
   * @param {string} value
   * @return {boolean}
   */
  function isSecretKeyValueOverLimit (value) {
    if (typeof value !== 'string') {
      return false
    }
    if (vm.ptg && vm.ptg.maskedToken && value === vm.ptg.maskedToken) {
      return false
    }
    return value.length > vm.secretKeyMaxLength
  }

  /**
   * True when the Secret Key model, control view/model, counter, or maxlength error exceeds 64.
   * Masked display (maskedToken) is not a real secret and is not treated as over-limit.
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @param {angular.INgModelController=} tokenCtrl form.token
   * @return {Boolean}
   */
  vm.isSecretKeyTooLong = function (tokenCtrl) {
    if (vm.ptg && vm.ptg.playbackTokenGeneratorType === 'CLOUDFRONT') {
      return false
    }
    if (vm.secretKeyCounterLength > vm.secretKeyMaxLength) {
      return true
    }
    if (isSecretKeyValueOverLimit(vm.ptg && vm.ptg.token)) {
      return true
    }
    if (!tokenCtrl) {
      return false
    }
    if (tokenCtrl.$error && tokenCtrl.$error.maxlength) {
      return true
    }
    if (isSecretKeyValueOverLimit(tokenCtrl.$viewValue)) {
      return true
    }
    if (isSecretKeyValueOverLimit(tokenCtrl.$modelValue)) {
      return true
    }
    return false
  }

  /**
   * Secret Key length gate for save — model, view, counter, and maxlength error.
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @param {Object} form
   * @return {Boolean}
   */
  vm.canSubmitSecretKey = function (form) {
    if (vm.ptg && vm.ptg.playbackTokenGeneratorType === 'CLOUDFRONT') {
      return true
    }
    return !vm.isSecretKeyTooLong(form && form.token)
  }

  /**
   * Whether Create/Update should be enabled (does not require full form validity until submit).
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @param {Object} form
   * @return {Boolean}
   */
  vm.canSubmitForm = function (form) {
    if (!form) {
      return false
    }
    if (!vm.canSubmitSecretKey(form)) {
      return false
    }
    if (!vm.isNew && form.$pristine) {
      return false
    }
    if (form.$submitted && form.$invalid) {
      return false
    }
    return true
  }

  /**
   * Form submit / Create / Update — always guarded; blocks Enter and click when over 64.
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   * @param {Event=} $event
   * @param {Object} form
   */
  vm.onFormSubmit = function ($event, form) {
    if ($event && $event.preventDefault) {
      $event.preventDefault()
    }
    if (!form) {
      return
    }
    if (!vm.canSubmitForm(form)) {
      return
    }
    form.$setSubmitted()
    if (!vm.canSubmitSecretKey(form)) {
      return
    }
    if (!vm.isNew && form.$pristine) {
      return
    }
    if (!form.$valid) {
      return
    }
    vm.submit(form.token)
  }

  /**
   * Create or edit Playback Token Generator, and close modal window afterwards
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   */
  vm.submit = function (tokenCtrl) {
    if (vm.isSecretKeyTooLong(tokenCtrl)) {
      return
    }
    vm.setDisable()
    ptg._save(data => {
      if (!['FLUSSONIC', 'ONAPP', 'AKAMAI_EDGIO', 'MULTI_CDN', 'CDN77', 'CACHEFLY'].includes(data.playbackTokenGeneratorType)) {
        delete data.ignoreClientIp
      }
    })
      .then(
        () => $uibModalInstance.close(ptg)
      )
      .finally(vm.unsetDisable)
  }

  vm.changeType = function () {
    if (!vm.ptg.id) {
      vm.ptg.tokenTime = vm.ptg.playbackTokenGeneratorType === 'CACHEFLY' ? 14400 : 3600
    }
  }

  /**
   * Cancel editing, close modal window
   *
   * @memberof mwApp/settings.controller:AddPlaybackTokenGeneratorCtrl
   */
  vm.cancel = function () {
    $uibModalInstance.dismiss('cancel')
  }
}
